import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  UserPlus,
  ArrowLeft,
  RefreshCcw,
  Save,
  Search,
  Hash,
  User,
  Calendar,
  Building2,
  Briefcase,
  BadgeCheck,
  BadgeX,
  FileText,
  Users,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

// dd/MM/yyyy validation + compare (like ASP)
function parseDDMMYYYY(str) {
  if (!str) return null;
  const s = String(str).trim();
  const parts = s.split("/");
  if (parts.length !== 3) return null;
  const dd = Number(parts[0]);
  const mm = Number(parts[1]);
  const yyyy = Number(parts[2]);
  if (!dd || !mm || !yyyy) return null;
  const d = new Date(yyyy, mm - 1, dd, 0, 0, 0);
  return Number.isFinite(d.getTime()) ? d : null;
}
function fmtDDMMYYYY(d) {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

const EmployeeJoinEntry = () => {

  const EMPTY_META = {
  transfers: [],
  empNames: [],
  empCodes: [],
  toSites: [],
  fromSites: [],
  departments: [],
  designations: [],
  defaultToSiteId: "",
};

const [meta, setMeta] = useState(EMPTY_META);

  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // Tailwind dynamic class note: your project already uses this pattern elsewhere.
  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO"; // "HO" or others

  // ASP uses Session["empid"] for edit; we use sessionStorage
  const joiningEmpId = sessionStorage.getItem("empid") || "";
  const isEdit = Boolean(String(joiningEmpId).trim());

  const screenBadge = useMemo(() => "INFRA SCREEN : C-19/1", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");


  const [loading, setLoading] = useState(false);

  // form (matches ASP fields)
  const [form, setForm] = useState({
    transferId: "Select", // ddl_tranno (value is transferId)
    entryDate: "", // dd/MM/yyyy
    empId: "", // employeeId (numeric)
    empCode: "", // text
    empNameTransferId: "Select", // ddl_empname value is ALSO transferId in ASP
    effectDate: "", // dd/MM/yyyy
    desigId: "Select",
    deptId: "Select",
    fromSiteId: "Select",
    toSiteId: "Select",
    reportTo: "",
    remarks: "",
    claMode: 0, // 0/1
    claAllowance: "0",
  });

  // View-only labels in Edit (ASP shows selected text)
  const [viewInfo, setViewInfo] = useState({
    empName: "",
    fromSiteName: "",
    toSiteName: "",
    deptName: "",
    desigName: "",
    transferCodeText: "",
  });

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const ensureBasics = () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return false;
    }
    if (!String(branchId).trim()) {
      toast.warn("branchId missing.");
      return false;
    }
    if (!String(branchType).trim() || branchType === "undefined") {
      toast.warn("branchType missing.");
      return false;
    }
    return true;
  };

  const loadMeta = async () => {
    if (!ensureBasics()) return;

    const res = await axios.get(`${BASE_URL}/api/payroll/employee-joining-entry/meta`, {
      params: {
        finYearId: Number(finYearId),
        branchId: Number(branchId),
        branchType: String(branchType),
      },
    });

    if (!res?.data?.success) throw new Error(res?.data?.message || "Meta load failed");

    const m = res?.data || {};
const nextMeta = {
  transfers: Array.isArray(m.transfers) ? m.transfers : [],
  empNames: Array.isArray(m.empNames) ? m.empNames : [],
  empCodes: Array.isArray(m.empCodes) ? m.empCodes : [],
  toSites: Array.isArray(m.toSites) ? m.toSites : [],
  fromSites: Array.isArray(m.fromSites) ? m.fromSites : [],
  departments: Array.isArray(m.departments) ? m.departments : [],
  designations: Array.isArray(m.designations) ? m.designations : [],
  defaultToSiteId: m.defaultToSiteId ?? "",
};

setMeta(nextMeta);

setForm((p) => ({
  ...p,
  toSiteId: String(m.defaultToSiteId || branchId),
}));

return nextMeta; // ✅ add this
  };

  // When TransferNo or EmployeeName changes -> fetch details and autofill
  const loadTransferDetails = async (transferId) => {
    if (!ensureBasics()) return;
    const tId = String(transferId || "").trim();
    if (!tId || tId === "Select") return;

    const res = await axios.get(`${BASE_URL}/api/payroll/employee-joining-entry/transfer-details`, {
      params: {
        finYearId: Number(finYearId),
        branchId: Number(branchId),
        transferId: Number(tId),
      },
    });

    if (!res?.data?.success) throw new Error(res?.data?.message || "Failed to load transfer details");

    const d = res.data.row;
    if (!d) return;

    // NOTE: SP column names depend on your DB/SP. These are based on your ASP usage.
    // Keep both: set ids + show text.
    setForm((p) => ({
      ...p,
      transferId: String(d.fld_employee_transferemployeeid || tId),
      empNameTransferId: String(d.fld_employee_transferemployeeid || tId),

      empId: String(d.empid || d.fld_employeeid || ""),
      empCode: String(d.empcode || d.fld_employeecode || ""),

      fromSiteId: String(d.brid || d.fld_branchid || "Select"),
      deptId: String(d.fld_deptid || "Select"),
      desigId: String(d.fld_desigid || "Select"),

      // ASP sets entryDate = fld_employee_effectdate (transfer effect date)
      entryDate: String(d.fld_employee_effectdate || ""),
    }));

    // view labels
    setViewInfo((p) => ({
      ...p,
      empName: String(d.employname || d.fld_employee || p.empName || ""),
      fromSiteName: String(d.branch || p.fromSiteName || ""),
      deptName: String(d.fld_departmentname || p.deptName || ""),
      desigName: String(d.fld_designationname || p.desigName || ""),
    }));
  };

  // Edit mode load: usp_INVBINDEmployeeJoinEditDetails + toSite
  const loadEdit = async () => {
    if (!isEdit) return;
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    const res = await axios.get(`${BASE_URL}/api/payroll/employee-joining-entry/${joiningEmpId}`, {
      params: { finYearId: Number(finYearId) },
    });

    if (!res?.data?.success) throw new Error(res?.data?.message || "Edit load failed");

    const row = res.data.row;
    if (!row) {
      toast.warn("Record not found.");
      return;
    }

    // Map fields from usp_INVBINDEmployeeJoinEditDetails (ASP uses these names in code-behind)
const toSite = res.data.toSite;

// ✅ Find transferId from meta.transfers using joinempcode (TRAN/0023)
const matchedTransfer = (meta?.transfers || []).find(
  (t) => String(t.code).trim() === String(row.joinempcode).trim()
);

// ✅ Find empNameTransferId (transferId) from meta.empNames using employeename
const matchedEmp = (meta?.empNames || []).find(
  (e) => String(e.employeeName).trim() === String(row.employeename).trim()
);
    setForm((p) => ({
  ...p,

  // ✅ IMPORTANT: set the dropdown values (NOT Select)
  transferId: String(matchedTransfer?.id || matchedEmp?.transferId || "Select"),
  empNameTransferId: String(matchedEmp?.transferId || matchedTransfer?.id || "Select"),

  // keep text fields
  empCode: String(row.employeecode || ""),
  entryDate: String(row.joindate || ""),
  effectDate: String(row.effectdate || ""),
  reportTo: String(row.report || ""),
  remarks: String(row.remarks || ""),

  // ✅ IMPORTANT: select dept/desig by ID (must match option value)
  deptId: String(row.deptid || "Select"),
  desigId: String(row.desigid || "Select"),

  // fromsite dropdown disabled in edit (ASP behavior)
  fromSiteId: "Select",

  // ✅ select toSite by ID (must match option value)
  toSiteId: String(toSite?.id || branchId),

  // ✅ CLA (your SP doesn’t return these; keep safe)
  claMode: Number(row.fld_CLAmode || 0),
  claAllowance: String(row.fld_CLAallowance || "0"),
}));

setViewInfo({
  empName: String(row.employeename || ""),
  fromSiteName: String(row.frombranch || ""),
  toSiteName: String(toSite?.name || ""),
  deptName: String(row.depnme || ""),
  desigName: String(row.designme || ""),
  transferCodeText: String(row.joinempcode || ""),
});

  };
useEffect(() => {
  let alive = true;

  (async () => {
    try {
      setLoading(true);

      // ✅ load meta first and get the returned meta object
      const loadedMeta = await loadMeta(); // make sure loadMeta returns nextMeta

      // ✅ edit must run AFTER meta is available
      if (isEdit) {
        await loadEdit(loadedMeta); // make sure loadEdit accepts loadedMeta
      } else {
        // default entry date = today (ASP user chooses, but safe default)
        if (alive) {
          setForm((p) => ({ ...p, entryDate: p.entryDate || fmtDDMMYYYY(new Date()) }));
        }
      }
    } catch (e) {
      toast.error(e?.message || "Load failed");
    } finally {
      if (alive) setLoading(false);
    }
  })();

  return () => {
    alive = false;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


  const resetForm = () => {
    // keep toSite default
    const defaultTo = String(meta.defaultToSiteId || branchId || "Select");
    setForm({
      transferId: "Select",
      entryDate: fmtDDMMYYYY(new Date()),
      empId: "",
      empCode: "",
      empNameTransferId: "Select",
      effectDate: "",
      desigId: "Select",
      deptId: "Select",
      fromSiteId: "Select",
      toSiteId: defaultTo,
      reportTo: "",
      remarks: "",
      claMode: 0,
      claAllowance: "0",
    });
    setViewInfo({
      empName: "",
      fromSiteName: "",
      toSiteName: "",
      deptName: "",
      desigName: "",
      transferCodeText: "",
    });
    toast.info("Cleared");
  };

  // validation like ASP Check_from_to + entry/effect rules
  const validate = () => {
    // Required like ASP
    if (isEdit) {
      // transfer / emp selection not needed because already joined
    } else {
      if (String(form.transferId) === "Select") return toast.warn("Select Transfer Code"), false;
      if (!String(form.empId).trim()) return toast.warn("Select Employee Code"), false;
      if (String(form.fromSiteId) === "Select") return toast.warn("Please select From Site.."), false;
    }

    if (String(form.toSiteId) === "Select") return toast.warn("Please select To Site.."), false;

    if (!String(form.entryDate).trim()) return toast.warn("Entry Date is required"), false;
    if (!String(form.effectDate).trim()) return toast.warn("Joining Effective Date is required"), false;

    const entry = parseDDMMYYYY(form.entryDate);
    const eff = parseDDMMYYYY(form.effectDate);
    if (!entry) return toast.warn("Entry Date format should be dd/MM/yyyy"), false;
    if (!eff) return toast.warn("Joining Effective Date format should be dd/MM/yyyy"), false;

    // entry & effect must not be future (ASP check in list, but keep safe)
    const today = new Date();
    const today00 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (entry.getTime() > today00.getTime()) return toast.warn("Select Entry Date lesser than or Equal to Current date"), false;
    if (eff.getTime() > today00.getTime()) return toast.warn("Select Joining Effective Date lesser than or Equal to Current date"), false;

    // ASP: effective >= entry
    if (eff.getTime() < entry.getTime())
      return toast.warn("Joining Effective Date Must greater than or Equal to Entry Date"), false;

    if (String(form.desigId) === "Select") return toast.warn("Designation is required"), false;
    if (String(form.deptId) === "Select") return toast.warn("Department is required"), false;

    if (!String(form.remarks).trim()) return toast.warn("Please Type the Reason"), false;

    // CLA allowance validation
    if (Number(form.claMode) === 1) {
      const n = Number(form.claAllowance);
      if (!Number.isFinite(n) || n < 0) return toast.warn("Invalid CLA Allowance"), false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);

      if (isEdit) {
        const res = await axios.put(
          `${BASE_URL}/api/payroll/employee-joining-entry/${joiningEmpId}`,
          {
            remarks: form.remarks,
            reportTo: form.reportTo,
            entryDate: form.entryDate,
            effectDate: form.effectDate,
            desigId: Number(form.desigId),
            deptId: Number(form.deptId),
            claMode: Number(form.claMode),
            claAllowance: Number(form.claMode) === 1 ? String(form.claAllowance || "0") : "0",
          },
          { params: { finYearId: Number(finYearId) } }
        );

        if (!res?.data?.success) {
          toast.error(res?.data?.message || "Update failed");
          return;
        }

        toast.success(res?.data?.message || "Updated Successfully...");
        sessionStorage.removeItem("empid");
        navigate("/PayRoll/EmployeeJoiningListing");
      } else {
        const res = await axios.post(
          `${BASE_URL}/api/payroll/employee-joining-entry`,
          {
            transferId: Number(form.transferId),
            empId: Number(form.empId),
            fromSiteId: Number(form.fromSiteId),
            toSiteId: Number(form.toSiteId),
            entryDate: form.entryDate,
            effectDate: form.effectDate,
            desigId: Number(form.desigId),
            deptId: Number(form.deptId),
            remarks: form.remarks,
            reportTo: form.reportTo,
            claMode: Number(form.claMode),
            claAllowance: Number(form.claMode) === 1 ? String(form.claAllowance || "0") : "0",
          },
          { params: { finYearId: Number(finYearId) } }
        );

        if (!res?.data?.success) {
          toast.error(res?.data?.message || "Save failed");
          return;
        }

        toast.success(res?.data?.message || "Joined Successfully...");
        navigate("/PayRoll/EmployeeJoiningList");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const onChangeTransfer = async (val) => {
    setForm((p) => ({ ...p, transferId: val, empNameTransferId: val }));
    try {
      setLoading(true);
      await loadTransferDetails(val);
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const onChangeEmpName = async (val) => {
    setForm((p) => ({ ...p, empNameTransferId: val, transferId: val }));
    try {
      setLoading(true);
      await loadTransferDetails(val);
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const backToList = () => {
    sessionStorage.removeItem("empid");
    navigate("/PayRoll/EmployeeJoiningList");
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE JOINING ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <UserPlus className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {isEdit ? "Edit Employee Joining" : "New Employee Joining"}
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Add / View / Edit (ASP behavior)
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              {isEdit && (
                <div
                  className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                    isDark ? "bg-amber-900/40 text-amber-100" : "bg-amber-100 text-amber-800"
                  }`}
                >
                  Edit ID: {joiningEmpId}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={backToList} className={ghostBtn} disabled={loading}>
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <button onClick={() => (isEdit ? loadEdit() : loadMeta())} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={resetForm} className={ghostBtn} disabled={loading}>
                <RefreshCcw className="w-4 h-4" />
                Add New
              </button>

              <button onClick={handleSubmit} className={primaryBtn} disabled={loading}>
                <Save className="w-4 h-4" />
                {isEdit ? "Update" : "Submit"}
              </button>
            </div>
          </div>

          {/* View summary (helps “view mode”) */}
         <div className={cardBase + " p-5 mb-6"}>
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    {/* Employee */}
    <div className={`rounded-2xl p-4 border ${isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"}`}>
      <div className="flex items-center gap-2 font-bold">
        <User className="w-4 h-4" /> Employee
      </div>
      <div className={`mt-2 text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
        {viewInfo?.empName || "—"}
      </div>
      <div className={`mt-1 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
        Code: {form?.empCode || "—"}
      </div>
    </div>

    {/* Sites */}
    <div className={`rounded-2xl p-4 border ${isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"}`}>
      <div className="flex items-center gap-2 font-bold">
        <Building2 className="w-4 h-4" /> Sites
      </div>

      <div className={`mt-2 text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
        From: {viewInfo?.fromSiteName || "—"}
      </div>

      <div className={`mt-1 text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
        To:{" "}
        {(
          (meta?.toSites || []).find((x) => String(x.id) === String(form?.toSiteId))?.name
        ) || viewInfo?.toSiteName || "—"}
      </div>
    </div>

    {/* Dept / Desig */}
    <div className={`rounded-2xl p-4 border ${isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"}`}>
      <div className="flex items-center gap-2 font-bold">
        <Briefcase className="w-4 h-4" /> Dept / Desig
      </div>

      <div className={`mt-2 text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
        Dept:{" "}
        {(
          (meta?.departments || []).find((x) => String(x.id) === String(form?.deptId))?.name
        ) || viewInfo?.deptName || "—"}
      </div>

      <div className={`mt-1 text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
        Desig:{" "}
        {(
          (meta?.designations || []).find((x) => String(x.id) === String(form?.desigId))?.name
        ) || viewInfo?.desigName || "—"}
      </div>
    </div>
  </div>
</div>


          {/* MAIN FORM */}
          <div className={cardBase + " p-6"}>
  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
    {/* Transfer Number */}
    <div>
      <label className={labelBase}>
        Transfer Number <span className="text-red-500">*</span>
      </label>
      <div className="mt-2 flex items-center gap-2">
        <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
        <select
          className={inputBase}
          value={String(form.transferId ?? "Select")}
          onChange={(e) => onChangeTransfer(e.target.value)}
          disabled={loading || isEdit}
          title={isEdit ? "Transfer cannot be changed in edit mode" : ""}
        >
          {/* ✅ in Edit: show current Transfer text even if not in meta */}
          {isEdit && String(form.transferId ?? "Select") === "Select" && (
            <option value="Select">{viewInfo?.transferCodeText || "Select"}</option>
          )}

          <option value="Select">Select</option>

          {(meta?.transfers || []).map((t) => (
            <option key={t.id} value={String(t.id)}>
              {t.code}
            </option>
          ))}
        </select>
      </div>

      {isEdit && (
        <div className={`mt-2 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          Transfer: {viewInfo?.transferCodeText || "—"}
        </div>
      )}
    </div>

    {/* Entry Date */}
    <div>
      <label className={labelBase}>
        Entry Date <span className="text-red-500">*</span>
      </label>
      <div className="mt-2 flex items-center gap-2">
        <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
        <input
          className={inputBase}
          value={form.entryDate ?? ""}
          onChange={(e) => setField("entryDate", e.target.value)}
          placeholder="dd/MM/yyyy"
          disabled={loading}
        />
      </div>
      <div className={`mt-1 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
        Tip: dd/MM/yyyy (example 06/02/2026)
      </div>
    </div>

    {/* Employee Code (readonly, filled by transfer details OR edit API) */}
    <div>
      <label className={labelBase}>
        Employee Code <span className="text-red-500">*</span>
      </label>
      <div className="mt-2 flex items-center gap-2">
        <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
        <input className={inputBase} value={form.empCode ?? ""} readOnly placeholder="Auto-filled" />
      </div>
    </div>

    {/* Joining Effective Date */}
    <div>
      <label className={labelBase}>
        Joining Effective Date <span className="text-red-500">*</span>
      </label>
      <div className="mt-2 flex items-center gap-2">
        <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
        <input
          className={inputBase}
          value={form.effectDate ?? ""}
          onChange={(e) => setField("effectDate", e.target.value)}
          placeholder="dd/MM/yyyy"
          disabled={loading}
        />
      </div>
    </div>

    {/* Employee Name (dropdown based on transfer) */}
    <div>
      <label className={labelBase}>
        Employee Name <span className="text-red-500">*</span>
      </label>
      <div className="mt-2 flex items-center gap-2">
        <Users className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
        <select
          className={inputBase}
          value={String(form.empNameTransferId ?? "Select")}
          onChange={(e) => onChangeEmpName(e.target.value)}
          disabled={loading || isEdit}
          title={isEdit ? "Employee cannot be changed in edit mode" : ""}
        >
          {/* ✅ in Edit: show current Employee name even if not in meta */}
          {isEdit && String(form.empNameTransferId ?? "Select") === "Select" && (
            <option value="Select">{viewInfo?.empName || "Select"}</option>
          )}

          <option value="Select">Select</option>

          {(meta?.empNames || []).map((x) => (
            <option key={x.transferId} value={String(x.transferId)}>
              {x.employeeName}
            </option>
          ))}
        </select>
      </div>
    </div>

    {/* Designation */}
    <div>
      <label className={labelBase}>
        Designation <span className="text-red-500">*</span>
      </label>
      <div className="mt-2 flex items-center gap-2">
        <Briefcase className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
        <select
          className={inputBase}
          value={String(form.desigId ?? "Select")}
          onChange={(e) => setField("desigId", e.target.value)}
          disabled={loading}
        >
          {/* ✅ if edit desigId not present in meta list, keep it visible */}
          {isEdit &&
            String(form.desigId ?? "Select") !== "Select" &&
            !(meta?.designations || []).some((d) => String(d.id) === String(form.desigId)) && (
              <option value={String(form.desigId)}>{viewInfo?.desigName || `(${form.desigId})`}</option>
            )}

          <option value="Select">Select</option>

          {(meta?.designations || []).map((d) => (
            <option key={d.id} value={String(d.id)}>
              {d.name}
            </option>
          ))}
        </select>
      </div>
    </div>

    {/* From Site */}
    <div>
      <label className={labelBase}>
        From Site <span className="text-red-500">*</span>
      </label>
      <div className="mt-2 flex items-center gap-2">
        <Building2 className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
        <select
          className={inputBase}
          value={String(form.fromSiteId ?? "Select")}
          onChange={(e) => setField("fromSiteId", e.target.value)}
          disabled={loading || isEdit} // in edit ASP it is text-only
          title={isEdit ? "From Site is fixed in edit mode" : ""}
        >
          {/* ✅ in Edit: show From Site name even if we don't have id */}
          {isEdit && String(form.fromSiteId ?? "Select") === "Select" && (
            <option value="Select">{viewInfo?.fromSiteName || "Select"}</option>
          )}

          <option value="Select">Select</option>

          {(meta?.fromSites || []).map((b) => (
            <option key={b.id} value={String(b.id)}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      {isEdit && (
        <div className={`mt-2 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          From Site: {viewInfo?.fromSiteName || "—"}
        </div>
      )}
    </div>

    {/* Department */}
    <div>
      <label className={labelBase}>
        Department <span className="text-red-500">*</span>
      </label>
      <div className="mt-2 flex items-center gap-2">
        <Briefcase className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
        <select
          className={inputBase}
          value={String(form.deptId ?? "Select")}
          onChange={(e) => setField("deptId", e.target.value)}
          disabled={loading}
        >
          {/* ✅ if edit deptId not present in meta list, keep it visible */}
          {isEdit &&
            String(form.deptId ?? "Select") !== "Select" &&
            !(meta?.departments || []).some((d) => String(d.id) === String(form.deptId)) && (
              <option value={String(form.deptId)}>{viewInfo?.deptName || `(${form.deptId})`}</option>
            )}

          <option value="Select">Select</option>

          {(meta?.departments || []).map((d) => (
            <option key={d.id} value={String(d.id)}>
              {d.name}
            </option>
          ))}
        </select>
      </div>
    </div>

    {/* To Site */}
    <div>
      <label className={labelBase}>
        To Site <span className="text-red-500">*</span>
      </label>
      <div className="mt-2 flex items-center gap-2">
        <Building2 className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
        <select
          className={inputBase}
          value={String(form.toSiteId ?? "Select")}
          onChange={(e) => setField("toSiteId", e.target.value)}
          disabled={loading}
        >
          {/* ✅ if edit toSiteId not present in meta list, keep it visible */}
          {isEdit &&
            String(form.toSiteId ?? "Select") !== "Select" &&
            !(meta?.toSites || []).some((b) => String(b.id) === String(form.toSiteId)) && (
              <option value={String(form.toSiteId)}>{viewInfo?.toSiteName || `(${form.toSiteId})`}</option>
            )}

          <option value="Select">Select</option>

          {(meta?.toSites || []).map((b) => (
            <option key={b.id} value={String(b.id)}>
              {b.name}
            </option>
          ))}
        </select>
      </div>
    </div>

    {/* Report To */}
    <div>
      <label className={labelBase}>
        Report To <span className="text-red-500">*</span>
      </label>
      <div className="mt-2 flex items-center gap-2">
        <User className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
        <input
          className={inputBase}
          value={form.reportTo ?? ""}
          onChange={(e) => setField("reportTo", e.target.value)}
          placeholder="Report to"
          disabled={loading}
        />
      </div>
    </div>

    {/* Remarks */}
    <div className="md:col-span-2">
      <label className={labelBase}>
        Remarks <span className="text-red-500">*</span>
      </label>
      <div className="mt-2 flex items-center gap-2">
        <FileText className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
        <input
          className={inputBase}
          value={form.remarks ?? ""}
          onChange={(e) => setField("remarks", e.target.value)}
          placeholder="Reason"
          disabled={loading}
        />
      </div>
    </div>

    {/* CLA Mode */}
    <div>
      <label className={labelBase}>
        CLA Mode <span className="text-red-500">*</span>
      </label>

      <div className="mt-3 flex items-center gap-6">
        <label className="inline-flex items-center gap-2">
          <input
            type="radio"
            name="claMode"
            checked={Number(form.claMode) === 1}
            onChange={() => setForm((p) => ({ ...p, claMode: 1 }))}
            disabled={loading}
          />
          <span className="font-bold">Yes</span>
          <BadgeCheck className="w-4 h-4" />
        </label>

        <label className="inline-flex items-center gap-2">
          <input
            type="radio"
            name="claMode"
            checked={Number(form.claMode) === 0}
            onChange={() => setForm((p) => ({ ...p, claMode: 0, claAllowance: "0" }))}
            disabled={loading}
          />
          <span className="font-bold">No</span>
          <BadgeX className="w-4 h-4" />
        </label>

        <input
          className={`${inputBase} max-w-[160px]`}
          value={form.claAllowance ?? "0"}
          onChange={(e) => setField("claAllowance", e.target.value)}
          disabled={loading || Number(form.claMode) !== 1}
          placeholder="Allowance"
        />
      </div>

      <div className={`mt-1 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
        If CLA = No, allowance will be saved as 0.
      </div>
    </div>

    {/* hidden empId display (debug) */}
    <div>
      <label className={labelBase}>Employee ID (hidden key)</label>
      <div className="mt-2 flex items-center gap-2">
        <Search className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
        <input className={inputBase} value={form.empId ?? ""} readOnly placeholder="Auto-filled" />
      </div>
    </div>
  </div>
</div>


          {/* footer buttons (same as ASP submit/addnew/back) */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-6">
            <button onClick={handleSubmit} className={primaryBtn} disabled={loading}>
              <Save className="w-4 h-4" />
              {isEdit ? "Update" : "Submit"}
            </button>

            <button onClick={resetForm} className={ghostBtn} disabled={loading}>
              <RefreshCcw className="w-4 h-4" />
              Add New
            </button>

            <button onClick={backToList} className={ghostBtn} disabled={loading}>
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeJoinEntry;
